import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import LandingNav from './LandingNav'
import LandingFooter from './LandingFooter'


// ── Demo content ─────────────────────────────────────────────────────────────

const FEATURES = [
  '2 rectangular pockets — 40×20mm, 8mm deep',
  '4 through-holes — ø6.8mm',
  '1 outside contour — 120×80mm',
  'Validated against PDF — 94% match',
]

const TOOLS = [
  { num: 'T01', type: 'Face Mill', dia: '50', note: 'Face stock to Z0' },
  { num: 'T02', type: 'Endmill', dia: '12', note: 'Rough + finish pockets & profile' },
  { num: 'T03', type: 'Endmill', dia: '6', note: 'Finish internal corners' },
  { num: 'T04', type: 'Drill', dia: '6.8', note: 'Drill 4× through-holes' },
]

const GCODE = [
  { code: 'G21 G90 G94', comment: 'Metric, absolute, feed/min' },
  { code: 'G17', comment: 'XY plane' },
  { code: 'T1 M06', comment: '50mm face mill' },
  { code: 'S4000 M03', comment: 'Spindle on CW' },
  { code: 'G00 X-30.0 Y-5.0 Z25.0', comment: 'Rapid to start' },
  { code: 'G43 H1 Z5.0', comment: 'Tool length comp' },
  { code: 'G01 Z0.0 F300', comment: 'Face pass' },
  { code: 'G01 X150.0 F1200', comment: 'Feed across stock' },
  { code: 'G00 Z25.0', comment: 'Retract' },
  { code: 'T2 M06', comment: '12mm endmill' },
  { code: 'S8000 M03', comment: 'Spindle on' },
  { code: 'G00 X15.0 Y15.0 Z5.0', comment: 'Rapid to pocket' },
  { code: 'G01 Z-2.0 F200', comment: 'Plunge into pocket' },
  { code: 'G01 X50.0 F800', comment: 'Pocket wall' },
]

// ── Helpers ──────────────────────────────────────────────────────────────────

function highlightGcode(code: string): string {
  return code
    .replace(/\b([GMT]\d+)\b/g, (m) => {
      if (m[0] === 'G') return `<span class="text-cyan-400">${m}</span>`
      return `<span class="text-pink-400">${m}</span>`
    })
    .replace(/\b([XYZR]-?\d+\.?\d*)\b/g, '<span class="text-slate-400">$1</span>')
    .replace(/\b([FS]\d+)\b/g, '<span class="text-amber-400">$1</span>')
    .replace(/\b([H]\d+)\b/g, '<span class="text-slate-400">$1</span>')
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StepSpinner() {
  return (
    <div className="w-3.5 h-3.5 border-[1.5px] border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin shrink-0" />
  )
}

function StepCheck() {
  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 500, damping: 25 }}
      className="w-3.5 h-3.5 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0"
    >
      <svg className="w-2 h-2 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </motion.div>
  )
}

function StepLabel({ label, done }: { label: string; done: boolean }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      {done ? <StepCheck /> : <StepSpinner />}
      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">
        {label}
      </span>
    </div>
  )
}

function PartDrawing() {
  return (
    <svg viewBox="0 0 120 80" className="w-20 h-14 shrink-0" fill="none" strokeWidth="0.8">
      <rect x="5" y="5" width="110" height="70" rx="2" className="stroke-cyan-500/30" />
      <rect x="12" y="12" width="38" height="18" rx="1" className="stroke-cyan-400/20" strokeDasharray="3,2" />
      <rect x="70" y="12" width="38" height="18" rx="1" className="stroke-cyan-400/20" strokeDasharray="3,2" />
      {[25, 50, 75, 100].map(cx => (
        <g key={cx}>
          <circle cx={cx} cy="55" r="3.5" className="stroke-cyan-400/20" />
          <line x1={cx - 2} y1="55" x2={cx + 2} y2="55" className="stroke-cyan-500/15" strokeWidth="0.3" />
          <line x1={cx} y1="53" x2={cx} y2="57" className="stroke-cyan-500/15" strokeWidth="0.3" />
        </g>
      ))}
    </svg>
  )
}

// ── Main component ───────────────────────────────────────────────────────────

export default function LandingPage() {
  const navigate = useNavigate()
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const scrollRef = useRef<HTMLDivElement>(null)

  // Block visibility
  const [showFiles, setShowFiles] = useState(false)
  const [showAnalysis, setShowAnalysis] = useState(false)
  const [showTools, setShowTools] = useState(false)
  const [showGcode, setShowGcode] = useState(false)

  // Done states
  const [filesDone, setFilesDone] = useState(false)
  const [analysisDone, setAnalysisDone] = useState(false)
  const [toolsDone, setToolsDone] = useState(false)
  const [gcodeDone, setGcodeDone] = useState(false)

  // Progressive reveal
  const [featureCount, setFeatureCount] = useState(0)
  const [toolCount, setToolCount] = useState(0)
  const [gcodeCount, setGcodeCount] = useState(0)

  // Celebration glow
  const [celebrating, setCelebrating] = useState(false)

  const sched = useCallback((fn: () => void, delay: number) => {
    timeoutsRef.current.push(setTimeout(fn, delay))
  }, [])

  const clearAll = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout)
    timeoutsRef.current = []
  }, [])

  const runDemo = useCallback(() => {
    clearAll()

    // ── Files ────────────────────────────────────
    sched(() => setShowFiles(true), 600)
    sched(() => setFilesDone(true), 2000)

    // ── Analysis ─────────────────────────────────
    sched(() => setShowAnalysis(true), 2500)
    FEATURES.forEach((_, i) => {
      sched(() => setFeatureCount(i + 1), 2900 + i * 450)
    })
    const analysisEnd = 2900 + FEATURES.length * 450 + 500
    sched(() => setAnalysisDone(true), analysisEnd)

    // ── Tools ────────────────────────────────────
    sched(() => setShowTools(true), analysisEnd + 400)
    TOOLS.forEach((_, i) => {
      sched(() => setToolCount(i + 1), analysisEnd + 700 + i * 400)
    })
    const toolsEnd = analysisEnd + 700 + TOOLS.length * 400
    sched(() => setToolsDone(true), toolsEnd + 400)

    // ── G-code ───────────────────────────────────
    sched(() => setShowGcode(true), toolsEnd + 600)
    GCODE.forEach((_, i) => {
      sched(() => setGcodeCount(i + 1), toolsEnd + 900 + i * 300)
    })
    const gcodeEnd = toolsEnd + 900 + GCODE.length * 300
    sched(() => setGcodeDone(true), gcodeEnd + 400)

    // ── Celebrate ────────────────────────────────
    sched(() => setCelebrating(true), gcodeEnd + 300)
    sched(() => setCelebrating(false), gcodeEnd + 1500)
  }, [clearAll, sched])

  // Auto-scroll terminal as content grows
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [featureCount, toolCount, gcodeCount, showFiles, showAnalysis, showTools, showGcode, gcodeDone])

  // Start demo on mount
  useEffect(() => {
    runDemo()
    return clearAll
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#030712] flex flex-col">
      <LandingNav />

      {/* ── Main content ──────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-10 pt-14">
        {/* Ambient glow */}
        <div
          className="fixed top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(6,182,212,0.04) 0%, transparent 70%)',
          }}
        />

        <div className="relative z-10 max-w-2xl w-full">
          {/* ── Brand + tagline ──────────────────────────────────────── */}
          <div className="text-center mb-8">
            <h1
              className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4"
              style={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 900,
                background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 20px rgba(6, 182, 212, 0.4))',
              }}
            >
              GCODR
            </h1>
            <p
              className="text-2xl md:text-4xl text-slate-200 mb-1"
              style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }}
            >
              Engineering Drawings to G-Code.
            </p>
            <p
              className="text-2xl md:text-4xl mb-6"
              style={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 800,
                background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Instantly.
            </p>
            <p className="font-mono text-sm text-slate-400 max-w-md mx-auto">
              Upload a DXF or PDF drawing, describe the job, and get verified G-code
              for your CNC mill. No CAM software required.
            </p>
          </div>

          {/* ── Terminal demo ─────────────────────────────────────────── */}
          <div
            className={`
              rounded-xl overflow-hidden mb-8 transition-all duration-500
              ${celebrating
                ? 'border border-cyan-500/40'
                : 'border border-cyan-500/20'
              }
            `}
            style={{
              background: 'rgba(255,255,255,0.02)',
              boxShadow: celebrating
                ? '0 0 80px rgba(6, 182, 212, 0.15), 0 0 160px rgba(6, 182, 212, 0.05)'
                : '0 0 60px rgba(6, 182, 212, 0.06)',
              transition: 'box-shadow 500ms ease-out, border-color 500ms ease-out',
            }}
          >
            {/* Title bar */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-cyan-500/10" style={{ background: 'rgba(255,255,255,0.015)' }}>
              <div className="w-2.5 h-2.5 rounded-full bg-red-400/50" />
              <div className="w-2.5 h-2.5 rounded-full bg-amber-400/50" />
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/50" />
              <span className="font-mono text-[10px] text-slate-600 ml-2">gcodr-terminal</span>
            </div>

            {/* Scrollable content area */}
            <div
              ref={scrollRef}
              className="p-5 space-y-4 overflow-y-auto"
              style={{
                maxHeight: '420px',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(6,182,212,0.2) transparent',
              }}
            >
              {/* ── FILES ──────────────────────────────────────────── */}
              {showFiles && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                >
                  <StepLabel label="Files" done={filesDone} />
                  <div className="ml-5.5 flex items-center gap-3" style={{ marginLeft: '22px' }}>
                    <PartDrawing />
                    <div className="space-y-0.5">
                      <div className="font-mono text-xs text-slate-300">bracket_mount.dxf</div>
                      <div className="font-mono text-xs text-slate-300">bracket_mount.pdf</div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ── ANALYSIS ───────────────────────────────────────── */}
              {showAnalysis && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                >
                  <StepLabel label="Analysis" done={analysisDone} />
                  <div className="space-y-1" style={{ marginLeft: '22px' }}>
                    {FEATURES.slice(0, featureCount).map((f, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45, ease: 'easeOut' }}
                        className={`font-mono text-xs ${
                          i === FEATURES.length - 1 ? 'text-emerald-400/80' : 'text-slate-400'
                        }`}
                      >
                        {f}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* ── TOOLS ──────────────────────────────────────────── */}
              {showTools && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                >
                  <StepLabel label="Tools" done={toolsDone} />
                  <div className="space-y-1.5" style={{ marginLeft: '22px' }}>
                    {TOOLS.slice(0, toolCount).map((t, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45, ease: 'easeOut' }}
                        className="flex items-baseline gap-2 font-mono text-xs"
                      >
                        <span className="text-cyan-500 shrink-0">{t.num}</span>
                        <span className="text-slate-300 shrink-0">{t.type}</span>
                        <span className="text-slate-500 shrink-0">{'\u2300'}{t.dia}mm</span>
                        <span className="text-slate-600 truncate">— {t.note}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* ── G-CODE ─────────────────────────────────────────── */}
              {showGcode && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                >
                  <StepLabel label="G-Code" done={gcodeDone} />
                  <div style={{ marginLeft: '22px' }}>
                    <pre className="font-mono text-xs leading-relaxed">
                      {GCODE.slice(0, gcodeCount).map((line, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, ease: 'easeOut' }}
                        >
                          <span dangerouslySetInnerHTML={{ __html: highlightGcode(line.code) }} />
                          <span className="text-slate-600"> ; {line.comment}</span>
                        </motion.div>
                      ))}
                      {showGcode && !gcodeDone && (
                        <span className="text-cyan-400 animate-pulse">{'\u258C'}</span>
                      )}
                    </pre>
                  </div>
                </motion.div>
              )}

              {/* ── Verification line ──────────────────────────────── */}
              {gcodeDone && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  style={{ marginLeft: '22px' }}
                >
                  <span className="font-mono text-xs text-emerald-400/80">
                    Program verified — {GCODE.length} lines, est. 2m 15s cycle time
                  </span>
                </motion.div>
              )}
            </div>
          </div>

          {/* ── Dual CTAs ────────────────────────────────────────────── */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-4 mb-4">
              <button
                onClick={() => navigate('/login')}
                className="
                  inline-flex items-center gap-2 px-8 py-3 rounded-lg
                  font-mono text-sm font-semibold
                  bg-gradient-to-r from-cyan-500 to-blue-500 text-gray-950
                  hover:from-cyan-400 hover:to-blue-400
                  transition-all duration-200
                "
                style={{ boxShadow: '0 0 20px rgba(6, 182, 212, 0.4)' }}
              >
                Get Started
              </button>
            </div>
            <p className="font-mono text-xs text-slate-600">
              Invite-only access during early preview.
              <br />
              Email <a href="mailto:contact@gcodr.dev" className="text-cyan-500/70 hover:text-cyan-400 transition-colors">contact@gcodr.dev</a> to join or request a preview.
            </p>
          </div>
        </div>
      </div>

      <LandingFooter />
    </div>
  )
}
